import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormikProps } from 'formik';
import { isFunction, isNull, map } from 'lodash';
import { useCallback } from 'react';

import { IModal, IModalFooterProps, IModalProperties } from './modal-renderer';
import { Button } from '../button/button';
import { FormValues } from '../form/form';
import {
  CardHeader,
  DialogActions,
  DialogContent,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';

interface IModalInnerPropsBase<TForm extends FormValues<TForm>> {
  modal: IModal<TForm>;
  modalProps: IModalProperties;
  formikProps?: FormikProps<TForm>;
}

interface IModalInnerPropsSave<TForm extends FormValues<TForm>>
  extends IModalInnerPropsBase<TForm> {
  formikProps: FormikProps<TForm>;
}

export type IModalInnerProps<TForm extends FormValues<TForm>> =
  | IModalInnerPropsBase<TForm>
  | IModalInnerPropsSave<TForm>;

export default function ModalInner<TForm extends FormValues<TForm>>({
  modal,
  modalProps,
  formikProps,
}: IModalInnerProps<TForm>) {
  const renderModalBody = useCallback(() => {
    if (isFunction(modal.body)) {
      if (
        (formikProps && modal.type === 'save') ||
        (formikProps && modal.type === 'readOnly')
      ) {
        return modal.body(formikProps);
      }
      if (modal.type === 'info') {
        return modal.body(modalProps);
      }

      throw new Error(`Modal type doesn't support a function body.`);
    }
    return modal.body;
  }, [formikProps, modal, modalProps]);

  const renderModalFooter = useCallback(() => {
    if (isFunction(modal.footer)) {
      return modal.footer({
        formikProps,
        modalProps,
        // @eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as IModalFooterProps<any>);
    }
    return modal.footer;
  }, [formikProps, modal, modalProps]);

  return (
    <>
      <CardHeader
        title={modal.title}
        avatar={!!modal.icon && <FontAwesomeIcon icon={modal.icon} />}
        action={
          <IconButton onClick={modalProps.onClose}>
            <Close />
          </IconButton>
        }
      />
      <DialogContent dividers autoFocus>
        {renderModalBody()}
      </DialogContent>
      <DialogActions>
        {modal.footer
          ? renderModalFooter()
          : !isNull(modal.footer) && (
              <>
                {map(modalProps.buttons, (button, idx) => (
                  <Button key={idx} {...button} />
                ))}
              </>
            )}
      </DialogActions>
    </>
  );
}
